import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Badge, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, Divider, IconButton, List,
  ListItemButton, Popover, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import { api, errorMessage } from "@/api/client";
import { endpoints, type Paginated } from "@/api/endpoints";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { notify } from "@/store/toast";
import { timeAgo, type NotificationPreferences, type NotificationRow } from "@/features/workflow/types";
import { currentSubscription, disablePush, enablePush, pushSupported, type PushConfig } from "@/push";

/** Often enough that an approval request is seen within a minute; rare enough to cost nothing. */
const POLL_MS = 60_000;

type Channel = "InApp" | "Email" | "Push";

/**
 * The bell: unread count, the latest notifications, and the preferences behind them.
 *
 * <p>Polls a single count rather than the list — the count is what the bar shows all day, the list
 * only matters when somebody opens it.</p>
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const unread = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: async () => (await api.get<{ count: number }>(endpoints.notifications.unreadCount)).data.count,
    refetchInterval: POLL_MS,
  });

  const list = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: async () =>
      (await api.get<Paginated<NotificationRow>>(endpoints.notifications.list, { params: { pageSize: 15 } })).data.items,
    enabled: Boolean(anchor),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["notifications"] });

  const markRead = useMutation({
    mutationFn: async (id: string) => api.post(endpoints.notifications.read(id)),
    onSuccess: refresh,
  });

  const markAll = useMutation({
    mutationFn: async () => api.post(endpoints.notifications.readAll),
    onSuccess: refresh,
    onError: (error) => notify.error(errorMessage(error)),
  });

  const open = (row: NotificationRow) => {
    if (!row.readAt) markRead.mutate(row.id);
    setAnchor(null);
    if (row.link) navigate(row.link);
  };

  const count = unread.data ?? 0;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label={`${count} unread notifications`}>
          <Badge color="error" badgeContent={count} max={99}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: "calc(100vw - 24px)" } } }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>Notifications</Typography>
          <Button size="small" disabled={count === 0 || markAll.isPending} onClick={() => markAll.mutate()}>
            Mark all read
          </Button>
          <Tooltip title="What I am told about">
            <IconButton size="small" onClick={() => { setAnchor(null); setPreferencesOpen(true); }}>
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Divider />

        <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
          {list.isPending ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>Loading…</Typography>
          ) : (list.data ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
              Nothing yet. Approvals, swaps and roster changes will show here.
            </Typography>
          ) : (
            <List disablePadding>
              {list.data!.map((row) => (
                <ListItemButton
                  key={row.id}
                  onClick={() => open(row)}
                  sx={{ alignItems: "flex-start", gap: 1.25, py: 1.25, borderBottom: 1, borderColor: "divider" }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 8, height: 8, borderRadius: "50%", mt: 0.75, flexShrink: 0,
                      bgcolor: row.readAt ? "transparent" : "primary.main",
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={row.readAt ? 400 : 600}>
                      {row.subject || row.eventType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {row.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{timeAgo(row.queuedAt)}</Typography>
                  </Box>
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Popover>

      {preferencesOpen && <PreferencesDialog onClose={() => setPreferencesOpen(false)} />}
    </>
  );
}

/**
 * What I would rather not be told. Everything is on until switched off — an opt-out, so a new kind
 * of notification reaches people without them having to find a setting first.
 */
function PreferencesDialog({ onClose }: { onClose: () => void }) {
  const preferences = useQuery({
    queryKey: ["notifications", "preferences"],
    queryFn: async () => (await api.get<NotificationPreferences>(endpoints.notifications.preferences)).data,
  });

  const [changes, setChanges] = useState<Record<string, boolean>>({});

  const save = useMutation({
    mutationFn: async () =>
      api.put(endpoints.notifications.preferences, {
        changes: Object.entries(changes).map(([key, isEnabled]) => {
          const [eventType, channel] = key.split("|");
          return { eventType, channel, isEnabled };
        }),
      }),
    onSuccess: () => { notify.success("Preferences saved"); onClose(); },
    onError: (error) => notify.error(errorMessage(error)),
  });

  const value = (eventType: string, channel: Channel, fallback: boolean) =>
    changes[`${eventType}|${channel}`] ?? fallback;

  const toggle = (eventType: string, channel: Channel, next: boolean) =>
    setChanges((previous) => ({ ...previous, [`${eventType}|${channel}`]: next }));

  const emailAvailable = preferences.data?.emailAvailable ?? false;
  const pushAvailable = preferences.data?.pushAvailable ?? false;

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <ClosableDialogTitle onClose={onClose}>Notification preferences</ClosableDialogTitle>
      <DialogContent>
        {pushAvailable && <ThisDevice />}
        {!emailAvailable && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This hospital does not send email notifications, so only in-app choices are shown.
          </Typography>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tell me when</TableCell>
              <TableCell align="center">In app</TableCell>
              {emailAvailable && <TableCell align="center">Email</TableCell>}
              {pushAvailable && <TableCell align="center">Phone / desktop</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {(preferences.data?.events ?? []).map((event) => (
              <TableRow key={event.eventType}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{event.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{event.description}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={value(event.eventType, "InApp", event.inApp)}
                    onChange={(e) => toggle(event.eventType, "InApp", e.target.checked)}
                  />
                </TableCell>
                {emailAvailable && (
                  <TableCell align="center">
                    <Checkbox
                      checked={value(event.eventType, "Email", event.email)}
                      onChange={(e) => toggle(event.eventType, "Email", e.target.checked)}
                    />
                  </TableCell>
                )}
                {pushAvailable && (
                  <TableCell align="center">
                    <Checkbox
                      checked={value(event.eventType, "Push", event.push)}
                      onChange={(e) => toggle(event.eventType, "Push", e.target.checked)}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={Object.keys(changes).length === 0 || save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Push for this one phone or computer. Apart from the table because it is a different question: the
 * table says which events, this says whether this device receives them at all — decided per device,
 * by the person holding it.
 */
function ThisDevice() {
  const config = useQuery({
    queryKey: ["notifications", "push"],
    queryFn: async () => (await api.get<PushConfig>(endpoints.notifications.push)).data,
  });
  const [subscribed, setSubscribed] = useState<boolean | null>(null);

  useEffect(() => {
    currentSubscription().then((s) => setSubscribed(Boolean(s))).catch(() => setSubscribed(false));
  }, []);

  const change = useMutation({
    mutationFn: async (on: boolean) => {
      if (on) await enablePush(config.data!.publicKey!);
      else await disablePush();
      return on;
    },
    onSuccess: (on) => {
      setSubscribed(on);
      notify.success(on ? "Notifications switched on for this device" : "Notifications switched off for this device");
      void config.refetch();
    },
    onError: (error) => notify.error(errorMessage(error)),
  });

  if (!config.data?.available) return null;
  const supported = pushSupported();

  return (
    <Alert
      severity={subscribed ? "success" : "info"}
      variant="outlined"
      sx={{ mb: 2 }}
      action={supported && subscribed !== null && (
        <Button size="small" disabled={change.isPending} onClick={() => change.mutate(!subscribed)}>
          {subscribed ? "Switch off" : "Switch on"}
        </Button>
      )}
    >
      <b>This device.</b>{" "}
      {!supported
        ? "This browser cannot show notifications. On an iPhone or iPad, add HealNTrack HMS to the home screen and open it from there."
        : subscribed
          ? "Notifications appear here even when the app is closed."
          : "Switch on to be told here even when the app is closed."}
    </Alert>
  );
}
